import { useState, useEffect, useRef } from "react"
import "./index.css"
import { Terminal, Cpu, Brain, Bot, LineChart, Atom, Sun, Moon, Target, Rocket, Shield } from "lucide-react"
import { Facebook, Twitter, Linkedin, Youtube } from "lucide-react"
import {
  QuantumComputingPage,AIIntegrationPage,QuantumMLPage,GenerativeAIPage,PredictiveAnalyticsPage,QuantumCloudServicePage,
  ProductsPage,
  CareersPage,
} from "./ServicePages"
import { X } from "lucide-react"
import AOS from "aos"
import "aos/dist/aos.css"
import SDKSection from "./SDKSections";

function App() {
  const [currentPage, setCurrentPage] = useState("home")
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [videoKey, setVideoKey] = useState(0)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const form = useRef();

      const [currentSlide, setCurrentSlide] = useState(0);
      const lightModeImages = [
        "/assets/images/he.pho1.png",
        "/assets/images/he.pho2.png",
        "/assets/images/he.pho3.png",
        "/assets/images/he.pho4.png",
      ]
    
      const darkModeImages = [
        "/assets/images/ho.da.pho1.png",
        "/assets/images/ho.da.pho2.png",
        "/assets/images/ho.da.pho3.png",
        "/assets/images/ho.da.pho4.png",
      ]
    
      const heroImages = isDarkMode ? darkModeImages : lightModeImages
    
      useEffect(() => {
        const interval = setInterval(() => {
          setCurrentSlide((prevSlide) => (prevSlide + 1) % heroImages.length)
        }, 5000) // Change image every 5 seconds
    
        return () => clearInterval(interval)
      }, [heroImages])
    
      useEffect(() => {
        const handleInitialRoute = () => {
          setCurrentPage("home")
          window.history.pushState({ page: "home" }, "", "/")
        }
    
        handleInitialRoute()
    
        window.addEventListener("beforeunload", () => {
          window.history.pushState({ page: "home" }, "", "/")
        })
    
        const handlePopState = (event) => {
          const page = event.state?.page || "home"
          setCurrentPage(page)
        }
    
        window.addEventListener("popstate", handlePopState)
    
        return () => {
          window.removeEventListener("popstate", handlePopState)
          window.removeEventListener("beforeunload", handleInitialRoute)
        }
      }, [])

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Initialize AOS differently for mobile and desktop
  useEffect(() => {
    if (isMobile) {
      // Remove all AOS attributes on mobile
      document.querySelectorAll("[data-aos]").forEach((elem) => {
        elem.removeAttribute("data-aos")
      })
    } else {
      // Initialize AOS for desktop
      AOS.init({
        duration: 1200,
        once: false,
        offset: 100,
        mirror: true,
        disable: window.innerWidth < 768,
      })

      // Add refresh on scroll
      window.addEventListener("scroll", () => {
        AOS.refresh()
      })
    }

    return () => {
      window.removeEventListener("scroll", () => {
        AOS.refresh()
      })
    }
  }, [isMobile])

  // Handle page changes and ensure proper content visibility
  useEffect(() => {
    if (currentPage === "home") {
      if (isMobile) {
        // Remove AOS attributes only on mobile when returning to home
        document.querySelectorAll("[data-aos]").forEach((elem) => {
          elem.removeAttribute("data-aos")
        })
      }
    }
  }, [currentPage, isMobile])

  const navigateTo = (page) => {
    setCurrentPage(page)
    setIsMobileMenuOpen(false)
    window.history.pushState({ page }, "", `/${page}`)

    if (isMobile) {
      // Instant scroll for mobile
      window.scrollTo(0, 0)
      // Remove AOS attributes on mobile
      document.querySelectorAll("[data-aos]").forEach((elem) => {
        elem.removeAttribute("data-aos")
      })
    } else {
      // Smooth scroll for desktop
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  useEffect(() => {
    const hash = window.location.hash
    if (hash) {
      const sectionId = hash.substring(1)
      scrollToSection(sectionId)
    }
  }, [])

  const handleNavigation = (sectionId) => {
    setIsMobileMenuOpen(false)
    scrollToSection(sectionId)
    setCurrentPage("home")
    window.history.pushState(null, "", `#${sectionId}`)
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const navbar = document.querySelector(".navbar")
      const navbarHeight = navbar ? navbar.offsetHeight : 0

      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      })
    }
  }

  const navigateToser = (page, sectionId = null) => {
    setCurrentPage(page)
    setIsMobileMenuOpen(false)
    window.history.pushState({ page }, "", `/${page}`)
    if (page === "home") {
      if (sectionId) {
        scrollToSection(sectionId)
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [currentPage])

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("menu-open")
    } else {
      document.body.classList.remove("menu-open")
    }

    return () => {
      document.body.classList.remove("menu-open")
    }
  }, [isMobileMenuOpen])

  useEffect(() => {
    if (showTeamModal) {
      document.body.classList.add("modal-open")
    } else {
      document.body.classList.remove("modal-open")
    }
  }, [showTeamModal])

  useEffect(() => {
    document.body.classList.toggle("dark-theme", isDarkMode)
  }, [isDarkMode])

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev)
    setVideoKey((prev) => prev + 1)
  }

  const sendEmail = async (e) => {
    e.preventDefault();
  
    const formData = {
      from_name: e.target.from_name.value,
      to_name: e.target.to_name.value,
      email: e.target.email.value,
      phone: e.target.phone.value,
      purpose: e.target.purpose.value,
      message: e.target.message.value
    };
  
    try {
      const response = await fetch('http://localhost:5001/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
  
      const data = await response.json();
      
      if (data.success) {
        alert('Message sent successfully!');
        e.target.reset();
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const Navbar = () => (
    <nav className="navbar">
      <div className="brand-container">
        <img
          src={isDarkMode ? "/assets/images/dark logo.png" : "/assets/images/light logo.png"}
          alt="Revren QAI Logo"
          className="logo"
          onClick={() => {
            navigateTo("home")
            setIsMobileMenuOpen(false)
          }}
          style={{ cursor: "pointer" }}
        />
      </div>

      {/* Mobile theme toggle */}
      <button className="theme-toggle mobile-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
        {isDarkMode ? <Sun size={24} /> : <Moon size={24} />}
      </button>

      <div
        className={`nav-toggle ${isMobileMenuOpen ? "active" : ""}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div className="nav-links">
        <button className="nav-item" onClick={() => handleNavigation("about")}>
          About
        </button>
        <button className="nav-item" onClick={() => handleNavigation("services")}>
          Services
        </button>
        <button className="nav-item" onClick={() => navigateTo("products")}>
          Products
        </button>
        <button className="nav-item" onClick={() => navigateTo("careers")}>
          Careers
        </button>
        <button className="nav-item" onClick={() => handleNavigation("contact")}>
          Contact
        </button>
        {/* Desktop theme toggle */}
        <button className="theme-toggle desktop-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
          {isDarkMode ? <Sun size={24} /> : <Moon size={24} />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="nav-mobile">
          <button className="nav-item" onClick={() => handleNavigation("about")}>
            About
          </button>
          <button className="nav-item" onClick={() => handleNavigation("services")}>
            Services
          </button>
          <button className="nav-item" onClick={() => navigateTo("products")}>
            Products
          </button>
          <button className="nav-item" onClick={() => navigateTo("careers")}>
            Careers
          </button>
          <button className="nav-item" onClick={() => handleNavigation("contact")}>
            Contact
          </button>
          
        </div>
      )}
    </nav>
  )

  return (
    <div className={`container ${isDarkMode ? "dark-theme" : ""}`}>
      {/* Only show video on home page */}
      {currentPage === "home" && (
        <video
          key={videoKey}
          autoPlay
          loop
          muted
          playsInline
          className="background-video"
          onError={(e) => console.log("Video loading error:", e)}
        >
          <source
            src={isDarkMode ? "/assets/video/Comp_1_42.mp4" : "/assets/video/background.video2.mp4.mov"}
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
      )}

      <Navbar />

      {currentPage === "home" && (
        <>
      <header className="hero">
  {!isMobile && (
    <div className="hero-slideshow">
      {heroImages.map((image, index) => (
        <div 
          className={`hero-slide ${index === currentSlide ? 'active' : ''}`} 
          key={index}
          style={{
            backgroundImage: `url(${image})`,
            opacity: index === currentSlide ? 1 : 0,
            transition: 'opacity 1s ease-in-out'
          }}
        />
      ))}
    </div>
  )}
  <div
    className="hero-content"
    key={isDarkMode ? "dark" : "light"}
    data-aos="zoom-in"
    data-aos-duration="1000"
    data-aos-easing="ease-out-cubic"
  >
    <h2 className="hero-title" data-aos="fade-down" data-aos-delay="200">
      Empower Today with Quantum Intelligence
    </h2>
    <p className="hero-description" data-aos="fade-up" data-aos-delay="400">
      Merging quantum computing with artificial intelligence to solve complex problems
    </p>
    <button
      className="hero-btn"
      onClick={() => navigateToser("home", "services")}
      data-aos="fade-up"
      data-aos-delay="600"
    >
      Explore More
      <span className="btn-arrow">↓</span>
    </button>
  </div>
</header>

      <section id="about" className="about">
        <div className="about-content">
          <h3 className="about-title" data-aos="fade-up">
            About Revren QAI
          </h3>
          <p className="about-description" data-aos="fade-up">
          Revren QAI LLP is a subsidiary of Reva Technologies exploring the exciting possibilities of  
          Quantum  Artificial Intelligence(AI) and Quantum Machine Learning(ML).  
          Our goal is to learn, experiment, and develop innovative solutions  
          that can bring the benefits of quantum technology to real-world applications.  
          we are passionate about pushing  
          the boundaries of technology and contributing to the future of computing.
          </p>
          <div className="about-cards">
            <div className="about-card" data-aos="fade-right">
              <div className="about-card-icon">
                <Target size={40} />
              </div>
              <h4>Our Vision</h4>
              <p>
              To be a pioneering startup in the field of Quantum AI,  
              exploring new ideas, learning from research, and building a  
              foundation for future innovations.
              </p>
            </div>

            <div className="about-card" data-aos="fade-left">
              <div className="about-card-icon">
                <Rocket size={40} />
              </div>
              <h4>Our Mission</h4>
              <p>
              We aim to understand and experiment with quantum technologies  
              and AI to develop small but impactful solutions that can help  
              businesses and researchers take their first steps into this  
              emerging field.
              </p>
            </div>

            <div className="about-card" data-aos="fade-up">
              <div className="about-card-icon">
                <Shield size={40} />
              </div>
              <h4>Our Values</h4>
              <p>
                Innovation, Excellence, Integrity, and Collaboration drive everything we do at Revren QAI.
              </p>
            </div>
          </div>

          <button className="meet-team-btn" data-aos="fade-up" onClick={() => setShowTeamModal(true)}>
            Meet Our Team
          </button>
        </div>
      </section>

          <section id="services" className="services">
            <h3 className="services-title" data-aos="fade-up">
              Our Services
            </h3>
            <div className="service-cards">
              <div className="service-card" data-aos="fade-right" onClick={() => navigateTo("quantumComputing")}>
                <Terminal className="service-icon" />
                <h4 className="service-title">Quantum Computing Solutions</h4>
                <p className="service-description">Advanced quantum algorithms tailored for your computational needs</p>
              </div>
              <div className="service-card" data-aos="fade-down" onClick={() => navigateTo("aiIntegration")}>
                <Brain className="service-icon" />
                <h4 className="service-title">AI Integration</h4>
                <p className="service-description">Cutting-edge AI models optimized with quantum computing</p>
              </div>
              <div className="service-card" data-aos="fade-left" onClick={() => navigateTo("quantumML")}>
                <Cpu className="service-icon" />
                <h4 className="service-title">Quantum Machine Learning</h4>
                <p className="service-description">Next-generation ML algorithms powered by quantum systems</p>
              </div>
              <div className="service-card" data-aos="fade-right" onClick={() => navigateTo("generativeAI")}>
                <Bot className="service-icon" />
                <h4 className="service-title">Generative AI</h4>
                <p className="service-description">Advanced AI systems for creating content, designs, and solutions</p>
              </div>
              <div className="service-card" data-aos="fade-up" onClick={() => navigateTo("predictiveAnalytics")}>
                <LineChart className="service-icon" />
                <h4 className="service-title">Quantum Predictive Analytics</h4>
                <p className="service-description">
                  Quantum-enhanced forecasting delivers unparalleled insights for smarter decisions
                </p>
              </div>
              <div className="service-card" data-aos="fade-left" onClick={() => navigateTo("quantumCloudService")}>
                <Atom className="service-icon" />
                <h4 className="service-title">Quantum Cloud Service</h4>
                <p className="service-description">
                  Quantum-enhanced data analysis for unprecedented insights and optimization
                </p>
              </div>
            </div>
          </section>

          <SDKSection isDarkMode={isDarkMode} />

      <section id="contact" className="contact">
        <div className="container">
          <h3 className="contact-title" data-aos="fade-up">
            Get in Touch
          </h3>
          <form ref={form} onSubmit={sendEmail} className="contact-form">
            <div className="name-row" style={{ display: "flex", gap: "20px" }}>
              <div className="form-group" data-aos="fade-right" style={{ flex: 1 }}>
                <label htmlFor="from_name">First Name</label>
                <input type="text" id="firstName" name="from_name" className="input" required />
              </div>
              <div className="form-group" data-aos="fade-left" style={{ flex: 1 }}>
                <label htmlFor="to_name">Last Name</label>
                <input type="text" id="lastName" name="to_name" className="input" required />
              </div>
            </div>

            <div className="contact-row" style={{ display: "flex", gap: "20px" }}>
              <div className="form-group" data-aos="fade-right" style={{ flex: 1 }}>
                <label htmlFor="phone">Contact Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="input"
                  pattern="[0-9]{10}"
                  placeholder="Enter 10-digit number"
                  required
                />
              </div>
              <div className="form-group" data-aos="fade-left" style={{ flex: 1 }}>
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" className="input" required />
              </div>
            </div>

            <div className="form-group" data-aos="fade-up">
              <label htmlFor="purpose">Purpose</label>
              <select id="purpose" name="purpose" className="input" required>
                <option value="career">Career</option>
                <option value="consultation">Consultation</option>
                <option value="general-inquiry">General Inquiry</option>
              </select>
            </div>

            <div className="form-group" data-aos="fade-up">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" className="textarea" required></textarea>
            </div>

            <button type="submit" data-aos="fade-up" className="contact-btn">
              Send Message
            </button>
          </form>
        </div>
      </section>
        </>
      )}

      {currentPage === "quantumComputing" && <QuantumComputingPage isDarkMode={isDarkMode} />}
      {currentPage === "aiIntegration" && <AIIntegrationPage isDarkMode={isDarkMode} />}
      {currentPage === "quantumML" && <QuantumMLPage isDarkMode={isDarkMode} />}
      {currentPage === "generativeAI" && <GenerativeAIPage isDarkMode={isDarkMode} />}
      {currentPage === "predictiveAnalytics" && <PredictiveAnalyticsPage isDarkMode={isDarkMode} />}
      {currentPage === "quantumCloudService" && <QuantumCloudServicePage isDarkMode={isDarkMode} />}
      {currentPage === "products" && <ProductsPage isDarkMode={isDarkMode} />}
      {currentPage === "careers" && <CareersPage isDarkMode={isDarkMode} />}

      <section id="company-info" className="company-info">
        <div className="container-company">
          <div className="logo-container">
            <img
              src={isDarkMode ? "/assets/images/dark logo.png" : "/assets/images/fi logo.png"}
              alt="Company Logo"
              className="company-logo"
              onClick={() => {
                navigateTo("home")
                setIsMobileMenuOpen(false)
              }}
            />
          </div>

          <div className="description">
            <p>
              Revren QAI is a pioneer in quantum and AI technologies, reshaping industries through innovative solutions.
            </p>
            <p>Our mission is to empower industries with next-generation quantum-enhanced capabilities.</p>
          </div>

          <div className="address">
            <p>
            <strong>Address:</strong> Shop no. 44, Riddhi Siddhi Residency, plot No-53/54A,Sec-3,New Panvel,Navi Mumbai,410206,India. 
            </p>
            <p>
            <strong>Regd.Office:</strong> Reva Technologies, Z-302 Omkar Bramha CHS Adai, New Panvel, Navi Mumbai,410206,India.
            </p>
          </div>

          <div className="contact-section">
            <h4 className="section-contact-title">Contact Us</h4>
            <p>
              <strong>Email:</strong> hrrevrenqai@gmail.com
            </p>
            <p>
              <strong>Phone:</strong> 022-3186 3655/+ 91 9137647622
            </p>
          </div>

          <div className="contact-section">
            <h4 className="section-contact-title">Follow Us</h4>
            <div className="social-icons">
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer">
                <Facebook className="social-icon" />
              </a>
              <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer">
                <Twitter className="social-icon" />
              </a>
              <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer">
                <Linkedin className="social-icon" />
              </a>
              <a
                href="https://youtube.com/@revatech-ai4737?si=htUfJu4n2Y9pNTfM"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Youtube className="social-icon" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <p className="footer-text">©2025 REVREN QAI LLP . All rights reserved.</p>
      </footer>

      {showTeamModal && (
        <div className="team-modal" onClick={() => setShowTeamModal(false)}>
          <div className="team-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setShowTeamModal(false)}>
              <X size={24} />
            </button>

            <h3 className="team-section-title">Tech Visionary & Founders</h3>
            <div className="team-grid leadership-grid">
              <div className="team-member">
                <img src="/assets/images/team/leader.jpg" alt="Leadership 1" className="member-image" />
                <h4>Rahul Kapse</h4>
                <p>Founder & Director</p>
                <a href="https://www.linkedin.com/in/rahul-kapse-32779384/" target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <Linkedin size={20} />
                </a>
              </div>
              <div className="team-member">
                <img src="/assets/images/team/lead-mam.webp" alt="Leadership 2" className="member-image" />
                <h4>Vasanti Kapse</h4>
                <p>Co-founder</p>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>

            <h3 className="team-section-title">Creative Technologist</h3>
            <div className="team-grid">
              <div className="team-member">
              <img src="/assets/images/team/team 3.jpg" alt="Team Member 4" className="member-image" />
                <h4>Dr. Pavate Aruna Animisha</h4>
                <p>Research Consultant</p>
                <a href="https://www.linkedin.com/in/aruna-pavate-3a7524186/" target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <Linkedin size={20} />
                </a>
              </div>
              <div className="team-member">
                <img src="/assets/images/team/team 2.png" alt="Team Member 2" className="member-image" />
                <h4>Vinayak Pottigar</h4>
                <p>Research Associate</p>
                <a href="https://www.linkedin.com/in/vinayak-pottigar-b692367/" target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <Linkedin size={20} />
                </a>
              </div>
              <div className="team-member">
              <img src="/assets/images/team/team 1.png" alt="Team Member 1" className="member-image" />
                <h4>Sagar Yashvantrao</h4>
                <p> Research Associate</p>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <Linkedin size={20} />
                </a>
              </div>
              <div className="team-member">
                <img src="/assets/images/team/team 4.jpg" alt="Team Member 2" className="member-image" />
                <h4>Rohit Nakhate</h4>
                <p> Research Assistant</p>
                <a href="https://www.linkedin.com/in/rohit-nakhate-7940031ba/" target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default App